/**
 * CAPTCHA Widget Editor
 * Handles configuration for CAPTCHA verification controls
 * - Provider selection
 * - Challenge options
 * - Theme and styling
 */

import React from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';

interface CaptchaWidgetConfig {
  provider?: 'recaptcha-v2' | 'recaptcha-v3' | 'hcaptcha';
  siteKey?: string;
  secretKey?: string;
  theme?: 'light' | 'dark';
  size?: 'normal' | 'compact';
  type?: 'image' | 'audio';
  badge?: 'bottomright' | 'bottomleft' | 'inline';
  verificationEndpoint?: string;
  successCallback?: string;
  errorCallback?: string;
}

/**
 * Render function for CAPTCHA widget editor
 */
function renderCaptchaWidgetEditor(props: PropertyEditorProps) {
  const { control, onConfigChange } = props;
  const config = (control.config as CaptchaWidgetConfig) || {};

  const provider = config.provider ?? 'recaptcha-v2';
  const theme = config.theme ?? 'light';
  const size = config.size ?? 'normal';
  const type = config.type ?? 'image';
  const badge = config.badge ?? 'bottomright';

  const isRecaptchaV3 = provider === 'recaptcha-v3';

  return (
    <div className="af-properties-tab__section">
      <h4 className="af-properties-tab__subsection-title">CAPTCHA Widget Configuration</h4>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          CAPTCHA Provider
          <select
            value={provider}
            onChange={(e) => onConfigChange('provider', e.target.value)}
            className="af-properties-tab__select"
          >
            <option value="recaptcha-v2">Google reCAPTCHA v2</option>
            <option value="recaptcha-v3">Google reCAPTCHA v3 (invisible)</option>
            <option value="hcaptcha">hCaptcha</option>
          </select>
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Site Key
          <input
            type="text"
            value={config.siteKey || ''}
            onChange={(e) => onConfigChange('siteKey', e.target.value)}
            className="af-properties-tab__input"
            placeholder="Public site key from provider"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Secret Key
          <input
            type="password"
            value={config.secretKey || ''}
            onChange={(e) => onConfigChange('secretKey', e.target.value)}
            className="af-properties-tab__input"
            placeholder="Secret key for server validation"
          />
        </label>
      </div>

      {!isRecaptchaV3 && (
        <>
          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Theme
              <select
                value={theme}
                onChange={(e) => onConfigChange('theme', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Size
              <select
                value={size}
                onChange={(e) => onConfigChange('size', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="normal">Normal</option>
                <option value="compact">Compact</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Challenge Type
              <select
                value={type}
                onChange={(e) => onConfigChange('type', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="image">Image</option>
                <option value="audio">Audio</option>
              </select>
            </label>
          </div>

          <div className="af-properties-tab__group">
            <label className="af-properties-tab__label">
              Badge Position
              <select
                value={badge}
                onChange={(e) => onConfigChange('badge', e.target.value)}
                className="af-properties-tab__select"
              >
                <option value="bottomright">Bottom Right</option>
                <option value="bottomleft">Bottom Left</option>
                <option value="inline">Inline</option>
              </select>
            </label>
          </div>
        </>
      )}

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Verification Endpoint
          <input
            type="text"
            value={config.verificationEndpoint || ''}
            onChange={(e) => onConfigChange('verificationEndpoint', e.target.value)}
            className="af-properties-tab__input"
            placeholder="e.g., /api/captcha/verify"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Success Callback (optional)
          <input
            type="text"
            value={config.successCallback || ''}
            onChange={(e) => onConfigChange('successCallback', e.target.value)}
            className="af-properties-tab__input"
            placeholder="Function name or event handler"
          />
        </label>
      </div>

      <div className="af-properties-tab__group">
        <label className="af-properties-tab__label">
          Error Callback (optional)
          <input
            type="text"
            value={config.errorCallback || ''}
            onChange={(e) => onConfigChange('errorCallback', e.target.value)}
            className="af-properties-tab__input"
            placeholder="Function name or event handler"
          />
        </label>
      </div>

      <div className="af-properties-tab__info-box" style={{ marginTop: '20px', padding: '12px', backgroundColor: '#fff3cd', borderRadius: '4px', fontSize: '12px', color: '#856404', border: '1px solid #ffc107' }}>
        <strong>Setup Instructions:</strong>
        <ul style={{ margin: '6px 0 0 20px', paddingLeft: '0' }}>
          <li>Register your site with the provider (Google, hCaptcha)</li>
          <li>Copy the Site Key and Secret Key from the dashboard</li>
          <li>Server must verify responses at the verification endpoint</li>
          <li>reCAPTCHA v3 is invisible and returns a score (0.0-1.0)</li>
        </ul>
      </div>
    </div>
  );
}

/**
 * CaptchaWidgetEditor - Handles CAPTCHA widget control configuration
 */
export const CaptchaWidgetEditor: PropertyEditor = {
  type: 'captcha-widget',
  label: 'CAPTCHA Widget',
  description: 'Configure CAPTCHA verification with multiple providers',
  render: renderCaptchaWidgetEditor,
};
